import { idb } from "../db/index.ts";
import g from "./g.ts";
import local from "./local.ts";
import type { OwnerExpectations } from "../../../common/types.ts";

// N4-0 season expectation ("contract") derivation. Turns the owner's annual
// three-factor evaluation (wins / playoffs / money — see genMessage.ts) into a
// forward-looking set of goals at the start of a new season. Pure so it can be
// unit tested and reused.
export type DeriveOwnerExpectationsInput = {
	season: number;
	numGames: number;
	numPlayoffRounds: number;
	// Previous (completed) season, for grounding the ambition in recent form.
	prevGamesPlayed: number;
	prevWon: number;
	prevPlayoffRoundsWon: number; // < 0 means missed the playoffs
	// Sum of the previous season's ownerMood factors, for tone only.
	ownerTotal: number;
	inGracePeriod: boolean;
	// Whether finances count (budget enabled).
	budget: boolean;
};

const WIN_FRACTION: Record<OwnerExpectations["playoffTarget"], number> = {
	contend: 0.62,
	makePlayoffs: 0.5,
	develop: 0.4,
};

export const deriveOwnerExpectations = (
	input: DeriveOwnerExpectationsInput,
): OwnerExpectations => {
	const {
		season,
		numGames,
		prevGamesPlayed,
		prevWon,
		prevPlayoffRoundsWon,
		inGracePeriod,
		budget,
	} = input;

	const prevWinp =
		prevGamesPlayed > 0 ? prevWon / prevGamesPlayed : undefined;

	let playoffTarget: OwnerExpectations["playoffTarget"];
	if (prevPlayoffRoundsWon >= 2 || (prevWinp !== undefined && prevWinp >= 0.573)) {
		// Deep run last year or a strong record → the owner wants a contender.
		playoffTarget = "contend";
	} else if (
		prevPlayoffRoundsWon >= 0 ||
		prevWinp === undefined ||
		prevWinp >= 0.427
	) {
		// Made the playoffs, brand-new team, or within striking distance.
		playoffTarget = "makePlayoffs";
	} else {
		// Clearly out of contention → a building year.
		playoffTarget = "develop";
	}

	const winTarget = Math.round(numGames * WIN_FRACTION[playoffTarget]);

	return {
		season,
		numGames,
		winTarget,
		playoffTarget,
		moneyTarget: budget,
		gracePeriod: inGracePeriod,
	};
};

type ExpectationTone = "grace" | "warning" | "neutral";

const getExpectationTone = (
	inGracePeriod: boolean,
	ownerTotal: number,
): ExpectationTone => {
	if (inGracePeriod) {
		return "grace";
	}

	return ownerTotal < 0 ? "warning" : "neutral";
};

// English source text stored in the DB. Kept as stable, exact sentences so the
// UI reverse-translation (messageTranslations.ts) can localize them — same
// pattern as the annual owner letter.
const PLAYOFF_SENTENCE: Record<OwnerExpectations["playoffTarget"], string> = {
	contend: "I want us contending for the title this year.",
	makePlayoffs: "Get us back to the playoffs.",
	develop:
		"This is a building year — develop our young players and set us up for the future.",
};

const CLOSING_SENTENCE: Record<ExpectationTone, string> = {
	grace:
		"You're still new here, so I won't be too hard on you yet. But keep these goals in mind.",
	warning: "My patience is wearing thin. I need to see real results this season.",
	neutral: "Hit these targets and we'll get along just fine.",
};

export const buildExpectationText = (
	expectations: OwnerExpectations,
	tone: ExpectationTone,
): string => {
	const paragraphs: string[] = [];

	paragraphs.push(
		`Welcome to the ${expectations.season} season. Here's what I expect from you.`,
	);
	paragraphs.push(
		`Wins: aim for at least ${expectations.winTarget} wins this season.`,
	);
	paragraphs.push(`Playoffs: ${PLAYOFF_SENTENCE[expectations.playoffTarget]}`);

	if (expectations.moneyTarget) {
		paragraphs.push(
			"Finances: keep us in the black — don't lose money this year.",
		);
	}

	paragraphs.push(CLOSING_SENTENCE[tone]);

	return paragraphs.map((p) => `<p>${p}</p>`).join("");
};

// N4-0: send the owner's season-start expectation letter into the inbox. Mirrors
// genMessage.ts guards (no auto-play / spectator / multi-team) and is a no-op if
// this season's letter already exists (idempotent re-trigger).
const genExpectationMessage = async () => {
	if (
		local.autoPlayUntil ||
		g.get("spectator") ||
		g.get("userTids").length > 1
	) {
		return;
	}

	const season = g.get("season");
	const userTid = g.get("userTid");

	// Don't send twice for the same season.
	const existingMessages = await idb.getCopies.messages({}, "noCopyCache");
	if (
		existingMessages.some(
			(message) => message.expectations?.season === season,
		)
	) {
		return;
	}

	const prevSeason = await idb.cache.teamSeasons.indexGet(
		"teamSeasonsBySeasonTid",
		[season - 1, userTid],
	);

	const prevWon = prevSeason?.won ?? 0;
	const prevGamesPlayed = prevSeason
		? prevSeason.won +
			prevSeason.lost +
			(prevSeason.otl ?? 0) +
			(prevSeason.tied ?? 0)
		: 0;
	const prevPlayoffRoundsWon = prevSeason?.playoffRoundsWon ?? -1;
	const prevMood = prevSeason?.ownerMood ?? {
		money: 0,
		playoffs: 0,
		wins: 0,
	};
	const ownerTotal = prevMood.wins + prevMood.playoffs + prevMood.money;
	const inGracePeriod = season < g.get("gracePeriodEnd");

	const expectations = deriveOwnerExpectations({
		season,
		numGames: g.get("numGames"),
		numPlayoffRounds: g.get("numGamesPlayoffSeries", "current").length,
		prevGamesPlayed,
		prevWon,
		prevPlayoffRoundsWon,
		ownerTotal,
		inGracePeriod,
		budget: g.get("budget"),
	});

	const tone = getExpectationTone(inGracePeriod, ownerTotal);
	const text = buildExpectationText(expectations, tone);

	await idb.cache.messages.add({
		read: false,
		from: "The Owner",
		year: season,
		text,
		subject: "Season expectations",
		tid: userTid,
		expectations,
	});
};

export default genExpectationMessage;
